import { useEffect, useRef } from 'react';

export interface UseInfiniteScrollEdgesParams {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  canLoadOlder: boolean;
  isLoadingOlder: boolean;
  loadOlder: () => Promise<unknown> | undefined;
  canLoadNewer: boolean;
  isLoadingNewer: boolean;
  loadNewer: () => Promise<unknown> | undefined;
  topThreshold?: number;
  bottomThreshold?: number;
}

export function useInfiniteScrollEdges({
  scrollContainerRef,
  canLoadOlder,
  isLoadingOlder,
  loadOlder,
  canLoadNewer,
  isLoadingNewer,
  loadNewer,
  topThreshold = 300,
  bottomThreshold = 300
}: UseInfiniteScrollEdgesParams) {
  const isAtBottomRef = useRef(true);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    let rafId = 0;

    const onScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const { scrollTop, scrollHeight, clientHeight } = el;
        const atTop = scrollTop <= topThreshold;
        const distanceToBottom = scrollHeight - (scrollTop + clientHeight);
        const atBottom = distanceToBottom <= bottomThreshold;

        isAtBottomRef.current = atBottom;

        if (atTop && scrollTop > 0 && canLoadOlder && !isLoadingOlder) {
          void loadOlder();
        }
        if (atBottom && canLoadNewer && !isLoadingNewer) {
          void loadNewer();
        }
      });
    };

    // Initialize once to set initial state
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [
    scrollContainerRef,
    canLoadOlder,
    isLoadingOlder,
    loadOlder,
    canLoadNewer,
    isLoadingNewer,
    loadNewer,
    topThreshold,
    bottomThreshold
  ]);

  return { isAtBottomRef } as const;
}
